/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module UpdateModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { ITUpgradeNode, UpgradeNodeCollection } from 'ajs/modules/upgrade';
import { L10nService } from '@vmw/ngx-vip';
import { NodeType, UpgradeOpsType } from 'generated-types';
import { StateService } from '@uirouter/core';
import { StringService } from 'string-service';
import { Subject, takeUntil } from 'rxjs';
import { UpgradeStateGroup } from 'ng/modules/update/update.types';
import * as StateNames from 'state-names';

import { UpdateStore } from '../update.store';
import { UPGRADE_STATE_POLLING_INTERVAL } from '../../services/update.service';

import * as l10n from './upgrade-transcript-modal.l10n';
import './upgrade-transcript-modal.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * UpgradeNode collection params type.
 */
interface IUpgradeNodeCollectionParams {
    id: string;
    node_type: NodeType;
    dryrun?: boolean;
}

/**
 * @description
 *
 *    Modal Component to display Controller/SE-group upgrade progress/status and transcript.
 *
 * @author Aravindh Nagarajan, Deepak Chaudhary
 */
@Component({
    selector: 'upgrade-transcript-modal',
    templateUrl: './upgrade-transcript-modal.component.html',
})
export class UpgradeTranscriptModalComponent implements OnInit {
    /**
     * UUID of the node.
     */
    @Input()
    public nodeId: string;

    /**
     * Upgrade node type : controller/SE-group
     */
    @Input()
    public nodeType: NodeType;

    /**
     * Flag to find out if the flow type is dry run.
     */
    @Input()
    public isDryRunFlow = false;

    /**
     * To be fired on modal close.
     */
    @Output()
    public onClose: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Upgrade Node version.
     */
    public version: string;

    /**
     * Controller/SE-group upgradeNode instance.
     */
    public node: ITUpgradeNode;

    /**
     * Name of the node.
     */
    public nodeName: string;

    /**
     * Uuid for Dry-run System Report.
     */
    public systemReportUuid: string;

    /**
     * Upgrade operation (upgrade/patch/rollback) label's l10nKey.
     */
    public upgradeOperationLabel: string;

    /**
     * Subject used to unsubscribe observables.
     */
    private ngDestroyed$ = new Subject();

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Upgrade status hash.
     */
    public readonly upgradeStateHash = {
        ERROR: UpgradeStateGroup.GROUP_ERROR,
        SUCCESS: UpgradeStateGroup.GROUP_COMPLETED,
    };

    /**
     * Hash of UpgradeOpsType and its l10n keys.
     */
    private readonly upgradeOpsHash = {
        [UpgradeOpsType.PATCH]: this.l10nKeys.patchLabel,
        [UpgradeOpsType.ROLLBACK]: this.l10nKeys.rollbackLabel,
        [UpgradeOpsType.ROLLBACKPATCH]: this.l10nKeys.rollbackPatchLabel,
        [UpgradeOpsType.UPGRADE]: this.l10nKeys.updateLabel,
        [UpgradeOpsType.RESTORE]: this.l10nKeys.restoreLabel,
        [UpgradeOpsType.UPGRADE_DRYRUN]: this.l10nKeys.dryRunLabel,
    };

    /**
     * UpgradeNodeCollection instance.
     * Used to load upgrade Node.
     */
    private upgradeNodeCollection: UpgradeNodeCollection | null = null;

    /**
     * Flag to identify if the operation is of type Restore.
     */
    private isRestoreFlow = false;

    public constructor(
        @Inject('UpgradeNodeCollection')
        private readonly UpgradeNodeCollectionClass: typeof UpgradeNodeCollection,
        private readonly $state: StateService,
        private readonly l10nService: L10nService,
        private readonly stringService: StringService,
        private readonly updateStore: UpdateStore,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        // When called from seGroupCollection grid in se-page-update.component
        // nodeType will not be there.
        this.nodeType = this.nodeType || NodeType.NODE_SE_GROUP;

        this.getUpgradeNodeInstance()
            .then(() => {
                this.nodeName = this.node.getName();
                this.version = this.node.getVersion() || this.node.getDryrunImageRefVersion();

                const { system_report_refs: systemReportRefs } = this.node?.getConfig() ?? {};
                const systemReportRef = systemReportRefs?.[0];

                if (systemReportRef) {
                    this.systemReportUuid = this.stringService.slug(systemReportRef);
                }

                if (this.upgradeState === this.upgradeStateHash.ERROR) {
                    this.node.getUpgradeFailureImageRefVersion();
                }

                this.setUpgradeOpsLabel();

                // If upgrade operation is not in terminal state (ie update is still in-progress)
                // set UpgradeNodeCollection instance with updateInterval to keep polling.
                // Once collection is loaded, update this.node with the latest.
                if (!this.isInTerminalState) {
                    this.setUpgradeNodePolling();
                }
            });

        this.updateStore.isRestoreFlow$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(
                isRestoreFlow => this.isRestoreFlow = isRestoreFlow,
            );
    }

    /**
     * Return whether upgrade completed or not.
     */
    public get isInTerminalState(): boolean {
        if (this.isDryRunFlow) {
            return !this.node.isDryrunInProgress();
        }

        return !this.node.isUpgradeInProgress();
    }

    /**
     * Getter for node type label.
     */
    public get nodeTypeLabel(): string {
        return this.nodeType === NodeType.NODE_CONTROLLER_CLUSTER ?
            this.l10nService.getMessage(this.l10nKeys.controllerLabel) :
            this.l10nService.getMessage(this.l10nKeys.seGroupLabel);
    }

    /**
     * Getter for node's progress.
     */
    public get upgradeProgress(): number {
        const {
            isDryRunFlow,
            node,
        } = this;

        return isDryRunFlow ? node.dryrunProgressPercentage : node.progressPercentage;
    }

    /**
     * Getter for upgrade end time.
     */
    public get upgradeEndTime(): string {
        const {
            isDryRunFlow,
            node,
        } = this;

        return isDryRunFlow ? node.dryrunOperationEndTime : node.upgradeEndTime;
    }

    /**
     * Getter for modal header text.
     *
     * If this.node is loaded, returns internationalized headerText
     * returns empty string otherwise.
     */
    public get modalHeaderText(): string {
        if (this.node) {
            const headerLabel = this.l10nService.getMessage(this.l10nKeys.headerLabel, [
                this.version,
            ]);

            const upgradeOperationLabel = this.l10nService.getMessage(this.upgradeOperationLabel);

            return `${upgradeOperationLabel} ${headerLabel}`;
        } else {
            return '';
        }
    }

    /**
     * Getter for upgrade state.
     */
    public get upgradeState(): string {
        const {
            isDryRunFlow,
            node,
        } = this;

        const stateGroup = isDryRunFlow ? node.dryrunStateGroup :
            node.stateGroup;

        switch (stateGroup) {
            case UpgradeStateGroup.GROUP_ERROR:
                return this.upgradeStateHash.ERROR;

            case UpgradeStateGroup.GROUP_COMPLETED:
                return this.upgradeStateHash.SUCCESS;

            default:
                return '';
        }
    }

    /**
     * Return upgrade transcript as string.
     */
    public get transcript(): string {
        const upgradeEvents = this.isDryRunFlow ? this.node.dryrunUpgradeEvents :
            this.node.upgradeEvents;

        return JSON.stringify(upgradeEvents, null, 4);
    }

    /**
     * Set the uuid for dry-run report.
     */
    public setSystemReportUuid(id: string): void {
        this.systemReportUuid = id;
    }

    /**
     * Navigate to System Reports details page.
     */
    public redirectToReportsPage(event: Event): void {
        event.preventDefault();
        this.onClose.emit();

        const params: Record<string, string> = {
            reportId: this.systemReportUuid,
        };

        this.$state.go(StateNames.REPORTS_DETAILS_SYSTEM_READINESS, params);
    }

    /**
     * Return the label to be displayed above progress bar.
     */
    public get progressBarHeaderLabel(): string {
        const {
            isDryRunFlow,
            isRestoreFlow,
            l10nKeys,
        } = this;

        if (isDryRunFlow) {
            return l10nKeys.dryRunProgressLabel;
        } else if (isRestoreFlow) {
            return l10nKeys.restoreLabel;
        } else {
            return l10nKeys.upgradeProgressBarLabel;
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        if (this.upgradeNodeCollection) {
            this.upgradeNodeCollection.destroy();
        }

        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Set Upgrade operation UI Label.
     */
    private setUpgradeOpsLabel(): void {
        const {
            isDryRunFlow,
            node,
        } = this;

        const upgradeOps = isDryRunFlow ? node.getConfig().dryrun_info.operation :
            node.getConfig().upgrade_ops;

        this.upgradeOperationLabel = this.upgradeOpsHash[upgradeOps];
    }

    /**
     * Load upgradeNode collection and sets this.node.
     */
    private async getUpgradeNodeInstance(): Promise<void> {
        await this.loadUpgradeNodeCollection();

        // Set this.node from the response from collection.
        this.setUpgradeNode();
    }

    /**
     * Load upgradeNodeCollection with id & node_type.
     */
    private loadUpgradeNodeCollection(): Promise<void> {
        const { nodeId, nodeType } = this;

        const params: IUpgradeNodeCollectionParams = {
            id: nodeId,
            node_type: nodeType,
        };

        if (this.isDryRunFlow) {
            params.dryrun = true;
        }

        this.upgradeNodeCollection = new this.UpgradeNodeCollectionClass({
            params,
        });

        return this.upgradeNodeCollection.load();
    }

    /**
     * Set this.node from upgradeNodeCollection.
     */
    private setUpgradeNode = (): void => {
        this.node = this.upgradeNodeCollection.getItemById(this.nodeId);
    };

    /**
     * Set updateInterval to poll for latest upgradeNode for in-progress state nodes.
     */
    private setUpgradeNodePolling = (): void => {
        this.upgradeNodeCollection.setUpdateInterval(UPGRADE_STATE_POLLING_INTERVAL);
    };
}
